import { ROUTES } from '../../appRoutes';
import { matchesRoutePath } from '../legacySiteLinks/legacySiteLinks';

function isLotteryPath(pathname: string): boolean {
  const discovery = ROUTES.LOTTO.DISCOVERY;
  return pathname === discovery || pathname.startsWith(`${discovery}/`);
}

type LegacyLotteryKey = keyof typeof ROUTES.LOTTO.LEGACY_ONLY;

type ShouldRedirectLotteryToLegacyOptions = {
  /** Legacy lotteries to treat as migrated for this request (e.g. enabled via a feature cookie). */
  enabledLotteries?: LegacyLotteryKey[];
};

function isMigratedLotteryPath(pathname: string, enabledLotteries: LegacyLotteryKey[]): boolean {
  const enabledPaths = enabledLotteries.map(key => ROUTES.LOTTO.LEGACY_ONLY[key]);

  return [...Object.values(ROUTES.LOTTO.MIGRATED), ...enabledPaths].some(migratedPath => matchesRoutePath(pathname, migratedPath));
}

function isLotteryChromaShellPath(pathname: string): boolean {
  const { DISCOVERY, BROWSE, PURCHASES, OFFERS, CATEGORIES } = ROUTES.LOTTO;

  if (pathname === DISCOVERY) {
    return true;
  }

  return [BROWSE, PURCHASES, OFFERS, ...Object.values(CATEGORIES)].some(shellPath => matchesRoutePath(pathname, shellPath));
}

/**
 * Determines if a lottery path should be redirected to the legacy lottery page based on the following criteria:
 * - The path is a lottery path.
 * - The path is not a lottery chroma shell path.
 * - The path is not a migrated lottery path.
 * @param pathname - The pathname to check.
 * @param options.enabledLotteries - Legacy lotteries to treat as migrated.
 * @returns True if the path should be redirected to the legacy lottery page, false otherwise.
 */
function shouldRedirectLotteryToLegacy(pathname: string, { enabledLotteries = [] }: ShouldRedirectLotteryToLegacyOptions = {}): boolean {
  if (!isLotteryPath(pathname)) {
    return false;
  }

  return !isLotteryChromaShellPath(pathname) && !isMigratedLotteryPath(pathname, enabledLotteries);
}

export { shouldRedirectLotteryToLegacy };
